import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, Bell, X, Loader2, AlertCircle, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { DateTime } from "luxon";
import { toast } from "sonner";
import {
  getMyReminders,
  cancelReminder,
  CheckpointReminder,
} from "../../api/checkpointReminders";
import useRetryFetch from "../../hooks/useRetryFetch";
import { CreateReminderModal } from "./CreateReminderModal";


interface RemindersTabProps {
  onClose: () => void;
}

export const RemindersTab: React.FC<RemindersTabProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();

  const [reminders, setReminders] = useState<CheckpointReminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    setIsLoading(true);
    try {
      const response = await getMyReminders(retryFetch, {
        status: "pending",
        includeArchived: false,
      });

      // Filter to only show pending reminders (extra safety check)
      // Exclude processing, sent, failed, or cancelled reminders
      const pendingReminders = response.reminders.filter(
        (r) => r.status === "pending"
      );

      // Sort by scheduled date time (earliest first)
      const sortedReminders = pendingReminders.sort(
        (a, b) =>
          new Date(a.scheduledDateTime).getTime() -
          new Date(b.scheduledDateTime).getTime()
      );
      setReminders(sortedReminders);
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
      toast.error(t("reminder_fetch_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReminder = async (reminderId: string) => {
    setCancellingId(reminderId);
    try {
      await cancelReminder(retryFetch, reminderId);
      toast.success(t("reminder_cancelled_success"));
      // Remove from list
      setReminders((prev) => prev.filter((r) => r._id !== reminderId));
    } catch (error) {
      console.error("Failed to cancel reminder:", error);
      toast.error(t("reminder_cancel_failed"));
    } finally {
      setCancellingId(null);
    }
  };

  const getReminderTypeLabel = (
    reminderType: "author_default" | "user_custom"
  ) => {
    return reminderType === "author_default"
      ? t("reminder_type_author")
      : t("reminder_type_user");
  };

  const getReminderActionLabel = (
    reminderAction: "resume_engagement" | "start_new_engagement"
  ) => {
    return reminderAction === "resume_engagement"
      ? t("reminder_type_resume")
      : t("reminder_type_start_new");
  };

  return (
    <>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b md:p-4 bg-background">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            <h3 className="text-base font-semibold">
              {t("reminders")}
            </h3>
            {reminders.length > 0 && (
              <span className="text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-medium">
                {reminders.length}
              </span>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => setIsCreateModalOpen(true)}
            className="h-8 gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t("create_reminder")}</span>
          </Button>
        </div>

      {/* Reminders list */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full p-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : reminders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-muted/50">
              <Bell className="w-8 h-8 opacity-50 text-muted-foreground" />
            </div>
            <h4 className="mb-1 text-sm font-medium">
              {t("no_upcoming_reminders")}
            </h4>
            <p className="text-xs text-muted-foreground max-w-[250px]">
              {t("set_reminder_from_activity")}
            </p>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="divide-y">
              {reminders.map((reminder) => {
                // Use app's language for date formatting
                const scheduledTime = DateTime.fromISO(reminder.scheduledDateTime).setLocale(i18n.language);
                const relativeTime = scheduledTime.toRelative();
                const formattedDate = scheduledTime.toLocaleString(
                  DateTime.DATETIME_MED
                );
                const isPast = scheduledTime < DateTime.now();

                return (
                  <div
                    key={reminder._id}
                    className="p-3 transition-colors md:p-4 hover:bg-accent/50 group"
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                          isPast
                            ? 'bg-orange-100 dark:bg-orange-900/20'
                            : 'bg-primary/10'
                        }`}>
                          <Bell className={`w-4 h-4 ${
                            isPast
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-primary'
                          }`} />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Activity Title */}
                        <h4 className="font-medium text-sm line-clamp-2 mb-1.5 leading-tight">
                          {reminder.activityTitle || t("untitled_activity")}
                        </h4>

                        {/* Scheduled Time */}
                        <div className="flex items-center gap-1.5 text-xs mb-1.5">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium text-foreground">{relativeTime}</span>
                        </div>
                        <div className="mb-2 text-xs text-muted-foreground">
                          {formattedDate}
                        </div>

                        {/* Reminder Details */}
                        <div className="flex items-center gap-2 text-xs">
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted whitespace-nowrap">
                            <Calendar className="flex-shrink-0 w-3 h-3" />
                            <span>{getReminderTypeLabel(reminder.reminderType)}</span>
                          </div>
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted whitespace-nowrap">
                            <span>{getReminderActionLabel(reminder.reminderAction)}</span>
                          </div>
                        </div>

                        {/* Warning for past reminders */}
                        {isPast && (
                          <div className="flex items-center gap-1 px-2 py-1 mt-2 text-xs text-orange-600 rounded dark:text-orange-400 bg-orange-50 dark:bg-orange-900/10 w-fit">
                            <AlertCircle className="flex-shrink-0 w-3 h-3" />
                            <span>{t("reminder_time_passed")}</span>
                          </div>
                        )}
                      </div>

                      {/* Cancel Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="flex-shrink-0 w-8 h-8 transition-opacity opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelReminder(reminder._id);
                        }}
                        disabled={cancellingId === reminder._id}
                        title={t("reminder_cancel_button")}
                      >
                        {cancellingId === reminder._id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>

    {/* Create Reminder Modal */}
    <CreateReminderModal
      open={isCreateModalOpen}
      onOpenChange={setIsCreateModalOpen}
      onReminderCreated={fetchReminders}
    />
    </>
  );
};
